import {
  AHIcon,
  AHQueryHandlerLoading,
  AHTeamIcon,
  AHText,
  Arrow,
  Box,
  TouchableBox,
  useLayoutContext,
  useTheme,
} from "arkh";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated";
import { FormattedMessage, useIntl } from "react-intl";
import React, { Suspense, useEffect, useMemo } from "react";

import { ScrollView, View } from "react-native";
import { useSizeClass } from "../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { TaskTypeIcon, TaskTypeIconSize } from "../../../types/TaskTypeIcon";
import { useIsFocused } from "@react-navigation/native";

import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useRefetchableFragment,
} from "react-relay";
import { RoutineDetailScreenQuery } from "./__generated__/RoutineDetailScreenQuery.graphql";
import { RoutineDetailScreenTaskActivity_Routine$key } from "./__generated__/RoutineDetailScreenTaskActivity_Routine.graphql";
import { RoutineDetailScreenTopTaskIssueCreator_Routine$key } from "./__generated__/RoutineDetailScreenTopTaskIssueCreator_Routine.graphql";
import { RoutineDetailScreenTopContributor_Routine$key } from "./__generated__/RoutineDetailScreenTopContributor_Routine.graphql";

import { DateTime } from "luxon";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";

import { IssueStatsTTRGraph } from "../../IssueStats/IssueStatsTTRGraph";

import { IssueStatsTopTypes } from "../../IssueStats/IssueStatsTopTypes";

import { IssueStatsCreatedGraph } from "../../IssueStats/IssueStatsCreatedGraph";
import { Header } from "./Header";
import { TeamIcon } from "../../TeamIdentifiers/teamIcons";
import {
  RoutineDetailNavigatorNavigationProp,
  RoutineDetailNavigatorScreenProps,
} from "./RoutineDetailStack";

function TopContributors(props: {
  routine: RoutineDetailScreenTopContributor_Routine$key;
}) {
  const intl = useIntl();

  const routine = useFragment(
    graphql`
      fragment RoutineDetailScreenTopContributor_Routine on Routine {
        topContributors {
          count
          user {
            id
            name
          }
        }
      }
    `,
    props.routine
  );
  return routine.topContributors.length === 0 ? null : (
    <Box spaceBottom="small">
      <Header
        icon="users"
        subtitle={intl.formatMessage({
          defaultMessage: "Users with the highest activity",
          description: "RoutineDetail: Leaderboard subtitle",
        })}
      >
        <FormattedMessage
          defaultMessage="Leaderboard"
          description="RoutineDetail: Leaderboard header"
        />
      </Header>
      <Box>
        {routine.topContributors.map((contributor, i) => (
          <Box key={contributor.user?.id} spaceBottom="small">
            <Box
              color="surface"
              cornerRadius="medium"
              space="medium"
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box style={{ flexDirection: "row", alignItems: "center" }}>
                {i === 0 ? (
                  <AHIcon
                    type="winner"
                    customColor="#ffc60a"
                    size="medium"
                    spaceRight="medium"
                  />
                ) : null}
                <AHText type="body-big">{contributor.user?.name}</AHText>
              </Box>
              <AHText type="subtitle" color="primary">
                {contributor.count}
              </AHText>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TopTaskIssueCreator(props: {
  routine: RoutineDetailScreenTopTaskIssueCreator_Routine$key;
  onPress: (
    taskId: string,
    taskTitle: string,
    section: null | {
      id: string;
      title: string;
      icon: TeamIcon | null;
    }
  ) => void;
}) {
  const intl = useIntl();

  const [routine] = useRefetchableFragment(
    graphql`
      fragment RoutineDetailScreenTopTaskIssueCreator_Routine on Routine
      @refetchable(queryName: "RoutineDetailScreenTaskIssueRefetch") {
        topTaskSectionIssueCreators(fromDate: $fromDate) {
          id
          section {
            id
            title
            assignedTeam {
              id
              icon
              name
            }
          }
          count
          task {
            id
            title
            ... on TaskCheck {
              __typename
            }
            ... on TaskNumber {
              __typename
            }
            ... on TaskText {
              __typename
            }
          }
        }
      }
    `,
    props.routine
  );

  return routine.topTaskSectionIssueCreators.length === 0 ? null : (
    <Box spaceBottom="medium">
      <Header
        icon="issue"
        subtitle={intl.formatMessage({
          defaultMessage: "Number of issues created on each task",
          description: "RoutineDetail: Task Issue Count subtitle",
        })}
      >
        <FormattedMessage
          defaultMessage="Task by Issue Count"
          description="RoutineDetail: Task Issue Count Header"
        />
      </Header>

      <Box>
        {routine.topTaskSectionIssueCreators.map(
          ({ task, id, count, section }) => {
            if (!task) {
              return null;
            }
            return (
              <TouchableBox
                key={id}
                spaceBottom="small"
                onPress={() => {
                  props.onPress(
                    task.id,
                    task.title,
                    section
                      ? {
                          id: section.id,
                          icon: section.assignedTeam?.icon
                            ? section.assignedTeam.icon
                            : null,
                          title:
                            (section.title || section.assignedTeam?.name) ?? "",
                        }
                      : null
                  );
                }}
              >
                <Box
                  color="surface"
                  cornerRadius="medium"
                  space="medium"
                  spaceRight="small"
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <TaskTypeIcon size={TaskTypeIconSize.SMALL} type={task} />
                    <Box style={{ flex: 1, justifyContent: "center" }}>
                      <AHText
                        type="body-big"
                        spaceHorizontal="small"
                        numberOfLines={1}
                      >
                        {task.title}
                      </AHText>
                      {section?.title || section?.assignedTeam?.name ? (
                        <Box
                          spaceHorizontal="small"
                          style={{ flexDirection: "row", alignItems: "center" }}
                        >
                          {section?.assignedTeam?.icon ? (
                            <AHTeamIcon
                              color="darkText"
                              size="xxsmall"
                              type={section.assignedTeam?.icon}
                              spaceRight="small"
                            />
                          ) : null}
                          <AHText
                            style={{ flex: 1 }}
                            type="detail"
                            color="darkText"
                            numberOfLines={1}
                          >
                            {section?.title || section.assignedTeam?.name}
                          </AHText>
                        </Box>
                      ) : null}
                    </Box>
                    <AHText type="subtitle" color="primary">
                      {count}
                    </AHText>
                    <AHIcon
                      spaceLeft="small"
                      type="chevron-right"
                      size="medium"
                    />
                  </Box>
                </Box>
              </TouchableBox>
            );
          }
        )}
      </Box>
    </Box>
  );
}

function Bar(props: { height: number }) {
  const theme = useTheme();
  const layout = useLayoutContext();
  const color = theme.primary;
  const borderRadius = layout.radius.small;

  const height = useSharedValue(0);
  const isOpen = useIsFocused();

  useEffect(() => {
    height.value = withSpring(isOpen ? props.height : 0, {
      mass: 1,
      damping: 14,
      velocity: 20,
    });
  }, [height, props.height, isOpen]);

  const style = useAnimatedStyle(
    () => ({
      backgroundColor: color,
      borderRadius,
      height: height.value,
    }),
    [height]
  );
  return <Animated.View style={style} />;
}

function TaskActivity(props: {
  routine: RoutineDetailScreenTaskActivity_Routine$key;
}) {
  const routine = useFragment(
    graphql`
      fragment RoutineDetailScreenTaskActivity_Routine on Routine {
        taskActivityByHour {
          count
          hour
        }
      }
    `,
    props.routine
  );
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const height = useMemo(() => (isCompact ? 150 : 250), [isCompact]);
  const intl = useIntl();

  const activityHours = useMemo(() => {
    const hours = Array.from({ length: isCompact ? 12 : 24 }).map((_, i) => {
      const hour = isCompact ? i * 2 + 1 : i;
      return {
        hour,
        count:
          routine?.taskActivityByHour.find(
            (a) => a.hour === hour || (isCompact && a.hour === hour - 1)
          )?.count ?? 0,
      };
    });
    const max = Math.max(...hours.map((h) => h.count)) ?? 1;
    return hours.map((h) => ({
      ...h,
      norm: h.count / (max > 0 ? max : 1),
    }));
  }, [isCompact, routine?.taskActivityByHour]);

  return routine.taskActivityByHour.length === 0 ? null : (
    <Box spaceBottom="medium">
      <Header
        icon="sun"
        subtitle={intl.formatMessage({
          defaultMessage: "Activity by time of day",
          description: "RoutineDetail: Routine Activity subtitle",
        })}
      >
        <FormattedMessage
          defaultMessage="Activity"
          description="RoutineDetail: Routine Activity Header"
        />
      </Header>
      <Box
        color="surface"
        cornerRadius="medium"
        spaceHorizontal="xsmall"
        spaceVertical="medium"
      >
        <View
          style={{
            height,
            flexDirection: "row",
            justifyContent: "space-evenly",
          }}
        >
          {activityHours.map(({ hour, norm }) => (
            <Box
              key={hour}
              style={{
                flex: 1,
                maxWidth: 30,
                height: "100%",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
              spaceHorizontal="xxsmall"
            >
              <Bar height={(height - 15) * norm} />
              <Box
                style={{
                  height: 15,
                  overflow: "hidden",
                  justifyContent: "flex-end",
                }}
              >
                <AHText
                  style={{ fontSize: 8, textAlign: "center" }}
                  size={8}
                  type="subtitle"
                  numberOfLines={1}
                >
                  {`0${hour}`.slice(-2)}
                </AHText>
              </Box>
            </Box>
          ))}
        </View>
      </Box>
    </Box>
  );
}

const RoutineDetail = (props: {
  navigation: RoutineDetailNavigatorNavigationProp<"RoutineDetailMain">;
  fetchKey: string;
  routineId: string;
  routineTitle: string;
  teamId?: string;
}) => {
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const data = useLazyLoadQuery<RoutineDetailScreenQuery>(
    graphql`
      query RoutineDetailScreenQuery($id: ID!, $fromDate: String) {
        routine(id: $id) {
          id
          ...RoutineDetailScreenTopContributor_Routine
          ...RoutineDetailScreenTopTaskIssueCreator_Routine
          ...RoutineDetailScreenTaskActivity_Routine
          issueStats {
            ...IssueStatsTopTypes_IssueStats
            ...IssueStatsCreatedGraph_IssueStats
            ...IssueStatsTTRGraph_IssueStats
          }
        }
      }
    `,
    {
      id: props.routineId,
      fromDate: DateTime.now().minus({ months: 30 }).toISODate(),
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (!data.routine) {
    return null;
  }
  return (
    <ScrollView
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={{
        paddingBottom: 35 + insets.bottom,
        paddingTop: layout.space.large,
      }}
    >
      <Box spaceHorizontal="medium">
        <Box spaceBottom="large">
          <TouchableBox
            color="primary"
            space="medium"
            cornerRadius="medium"
            style={{
              justifyContent: "space-between",
              flexDirection: "row",
              alignContent: "center",
              alignItems: "center",
            }}
            onPress={() => {
              props.navigation.navigateDeprecated("RoutinesOcassionsFollowup", {
                routineId: props.routineId,
                routineTitle: props.routineTitle,
                teamId: props.teamId,
              });
            }}
          >
            <AHText type="subtitle" color="lightText">
              <FormattedMessage
                defaultMessage="Overview"
                description="RoutineDetailListSubtitle: Overview"
              />
            </AHText>
            <Arrow color="#FFF" />
          </TouchableBox>
        </Box>
        <Suspense fallback={<AHQueryHandlerLoading />}>
          <Box style={{ flexDirection: "column" }}>
            <IssueStatsTopTypes issueStats={data.routine.issueStats} />
            <TopTaskIssueCreator
              routine={data.routine}
              onPress={(taskId, taskTitle, section) => {
                props.navigation.navigateDeprecated("TaskAllIssues", {
                  taskId,
                  taskTitle,
                  section,
                });
              }}
            />
            <IssueStatsTTRGraph rounded issueStats={data.routine.issueStats} />
            <IssueStatsCreatedGraph
              rounded
              issueStats={data.routine.issueStats}
            />
            <TaskActivity routine={data.routine} />
            <TopContributors routine={data.routine} />
          </Box>
        </Suspense>
      </Box>
    </ScrollView>
  );
};

export const RoutineDetailScreen = (
  props: RoutineDetailNavigatorScreenProps<"RoutineDetailMain">
) => {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <RoutineDetail
          fetchKey={fetchKey}
          navigation={props.navigation}
          routineId={props.route.params.routineId}
          routineTitle={props.route.params.routineTitle}
          teamId={props.route.params.teamId}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
};
